import type { FieldDto } from "../../types/field";
import type { SectionDto } from "../../types/section";
import type {
  ValuesByName,
  VisibilityConfig,
  VisibilityOperator,
  VisibilityRule,
} from "../../types/visibility";
import { resolveFieldVisibility } from "./fieldOptions";
import { parseCalendarDate, toIsoDate } from "./dateField";
import { pad2, parseIsoDateTime } from "../dates/isoDate";

const OPERATORS: VisibilityOperator[] = [
  "equals",
  "not_equals",
  "empty",
  "not_empty",
  "filled",
  "contains",
  "in",
  "gt",
  "gte",
  "lt",
  "lte",
];

export function normalizeValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "1" : "0";
  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }
  return String(value).trim();
}

export function normalizeVisibilityConfig(
  config: VisibilityConfig | Record<string, unknown> | null | undefined,
): VisibilityConfig | null {
  if (!config || typeof config !== "object") return null;

  const rules = config.rules;
  if (!Array.isArray(rules) || rules.length === 0) return null;

  let logic = String(config.logic ?? "all").toLowerCase();
  if (logic !== "all" && logic !== "any") logic = "all";

  let action = String(config.action ?? "show").toLowerCase();
  if (action !== "show" && action !== "hide") action = "show";

  const normalizedRules: VisibilityRule[] = [];

  for (const rule of rules) {
    if (!rule || typeof rule !== "object") continue;

    const field = String((rule as VisibilityRule).field ?? "").trim();
    const operator = String(
      (rule as VisibilityRule).operator ?? "",
    ).toLowerCase();

    if (!field || !OPERATORS.includes(operator as VisibilityOperator)) {
      continue;
    }

    normalizedRules.push({
      field,
      operator: operator as VisibilityOperator,
      value: (rule as VisibilityRule).value ?? null,
    });
  }

  if (normalizedRules.length === 0) return null;

  return {
    logic: logic as VisibilityConfig["logic"],
    action: action as VisibilityConfig["action"],
    rules: normalizedRules,
  };
}

function hasClockTime(value: string): boolean {
  return /[T ]\d{2}:\d{2}/.test(value.trim());
}

function dateCompareKey(value: unknown, withTime: boolean): string | null {
  const text = normalizeValue(value);
  const date = parseCalendarDate(text);
  if (!date) return null;
  const day = toIsoDate(date);
  if (!withTime) return day;

  let hours = 0;
  let minutes = 0;
  if (hasClockTime(text)) {
    const parsed = parseIsoDateTime(text);
    if (parsed) {
      hours = parsed.getHours();
      minutes = parsed.getMinutes();
    }
  }
  return `${day}T${pad2(hours)}:${pad2(minutes)}`;
}

export function evaluateRule(
  rule: VisibilityRule,
  valuesByName: ValuesByName,
): boolean {
  const current = normalizeValue(valuesByName[rule.field]);
  const expected = rule.value;
  const expectedText = normalizeValue(expected);
  const useTime = hasClockTime(current) || hasClockTime(expectedText);
  const currentKey = dateCompareKey(current, useTime);
  const expectedKey = dateCompareKey(expected, useTime);

  switch (rule.operator) {
    case "equals":
      if (currentKey && expectedKey) return currentKey === expectedKey;
      if (currentKey || expectedKey) return false;
      return current === expectedText;
    case "not_equals":
      if (currentKey && expectedKey) return currentKey !== expectedKey;
      if (currentKey || expectedKey) return true;
      return current !== expectedText;
    case "empty":
      return current === "";
    case "not_empty":
    case "filled":
      return current !== "";
    case "contains":
      return current !== "" && current.includes(expectedText);
    case "in": {
      const list = Array.isArray(expected) ? expected : [expected];
      if (currentKey) {
        return list.some(
          (item) => dateCompareKey(item, useTime) === currentKey,
        );
      }
      return list.map(normalizeValue).includes(current);
    }
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return compareOrdered(current, expected, rule.operator);
    default:
      return false;
  }
}

function compareOrdered(
  current: string,
  expected: unknown,
  operator: "gt" | "gte" | "lt" | "lte",
): boolean {
  if (current === "") return false;

  const expectedNorm = normalizeValue(expected);
  if (expectedNorm === "") return false;

  const useTime = hasClockTime(current) || hasClockTime(expectedNorm);
  const leftKey = dateCompareKey(current, useTime);
  const rightKey = dateCompareKey(expectedNorm, useTime);
  if (leftKey || rightKey) {
    if (!leftKey || !rightKey) return false;
    switch (operator) {
      case "gt":
        return leftKey > rightKey;
      case "gte":
        return leftKey >= rightKey;
      case "lt":
        return leftKey < rightKey;
      case "lte":
        return leftKey <= rightKey;
    }
  }

  const leftNum = Number(current);
  const rightNum = Number(expectedNorm);
  if (!Number.isNaN(leftNum) && !Number.isNaN(rightNum)) {
    switch (operator) {
      case "gt":
        return leftNum > rightNum;
      case "gte":
        return leftNum >= rightNum;
      case "lt":
        return leftNum < rightNum;
      case "lte":
        return leftNum <= rightNum;
    }
  }

  switch (operator) {
    case "gt":
      return current > expectedNorm;
    case "gte":
      return current >= expectedNorm;
    case "lt":
      return current < expectedNorm;
    case "lte":
      return current <= expectedNorm;
  }
}

export function isFieldVisible(
  field: FieldDto,
  valuesByName: ValuesByName,
): boolean {
  const config = resolveFieldVisibility(field);
  if (!config?.rules.length) return true;

  const results = config.rules.map((rule) => evaluateRule(rule, valuesByName));

  const matched =
    config.logic === "any" ? results.some(Boolean) : results.every(Boolean);

  return config.action === "hide" ? !matched : matched;
}

export function collectAllFields(
  sections: SectionDto[],
  rootFields: FieldDto[] = [],
): FieldDto[] {
  const fromSections = sections.flatMap((section) => section.fields ?? []);
  return [...(rootFields ?? []), ...fromSections].sort(
    (a, b) => a.order - b.order,
  );
}

/** Поля в порядке обхода чата: секции по `order`, внутри — поля по `order`. */
export function collectFieldsInFormOrder(
  sections: SectionDto[],
  rootFields: FieldDto[] = [],
): FieldDto[] {
  const roots = [...(rootFields ?? [])].sort((a, b) => a.order - b.order);
  const fromSections = [...sections]
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
    .flatMap((section) =>
      [...(section.fields ?? [])].sort((a, b) => a.order - b.order),
    );
  return [...roots, ...fromSections];
}

export function findFieldSection(
  sections: SectionDto[],
  fieldName: string,
): SectionDto | undefined {
  return sections.find((section) =>
    (section.fields ?? []).some((field) => field.name === fieldName),
  );
}

export function buildVisibleByName(
  fields: FieldDto[],
  valuesByName: ValuesByName,
): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const field of fields) {
    map[field.name] = isFieldVisible(field, valuesByName);
  }
  return map;
}

export function pruneHiddenFieldValues(
  fields: FieldDto[],
  valuesByName: ValuesByName,
): ValuesByName {
  const next: ValuesByName = { ...valuesByName };

  for (const field of fields) {
    if (!isFieldVisible(field, next)) {
      next[field.name] = "";
    }
  }

  return next;
}
